import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle,
  TrendingUp,
  Star,
  EmojiEvents,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface SuccessAreasProps {
  courseId: string;
}

const SuccessAreas: React.FC<SuccessAreasProps> = ({ courseId }) => {
  // Mock data
  const highPassRateTopics = [
    {
      topicId: 1,
      topicName: 'Linear Equations',
      passRate: 92,
      avgScore: 85,
      successFactors: ['Clear explanations', 'Good examples', 'Appropriate difficulty'],
    },
    {
      topicId: 2,
      topicName: 'Quadratic Functions',
      passRate: 85,
      avgScore: 78,
      successFactors: ['Interactive content', 'Step-by-step solutions'],
    },
  ];

  const quickMasteryTopics = [
    {
      topicId: 1,
      topicName: 'Linear Equations',
      completionTime: 38,
      estimatedTime: 45,
      avgScore: 85,
      masteryRate: 92,
    },
    {
      topicId: 2,
      topicName: 'Quadratic Functions',
      completionTime: 52,
      estimatedTime: 60,
      avgScore: 78,
      masteryRate: 85,
    },
  ];

  const bestPractices = [
    {
      practice: 'Clear Step-by-Step Solutions',
      description: 'Topics with detailed step-by-step solutions show 15% higher pass rates',
      applicableTopics: ['Linear Equations', 'Quadratic Functions'],
    },
    {
      practice: 'Interactive Content',
      description: 'Topics with interactive elements have 20% higher engagement',
      applicableTopics: ['Quadratic Functions', 'Polynomials'],
    },
    {
      practice: 'Appropriate Difficulty Progression',
      description: 'Well-sequenced topics show better completion rates',
      applicableTopics: ['Linear Equations', 'Quadratic Functions'],
    },
  ];

  const successData = [
    { topic: 'Linear Equations', passRate: 92, avgScore: 85 },
    { topic: 'Quadratic Functions', passRate: 85, avgScore: 78 },
  ];

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Success Areas
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Identify what's working well to replicate successful elements
      </Typography>

      <Grid container spacing={3}>
        {/* High Pass Rate Topics */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircle color="success" />
              <Typography variant="h6">
                High Pass Rate Topics
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={successData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="topic" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="passRate" fill="#82ca9d" name="Pass Rate %" />
                <Bar dataKey="avgScore" fill="#8884d8" name="Avg Score %" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Quick Mastery Stats */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TrendingUp color="success" />
              <Typography variant="h6">
                Quick Mastery Topics
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Topics completed in &lt;50% estimated time with high scores
              </Typography>
              <Typography variant="h4" mt={2} fontWeight="bold">
                2 topics
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* High Pass Rate Details */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              High Performing Topics Details
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Pass Rate</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell>Success Factors</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highPassRateTopics.map((topic) => (
                    <TableRow key={topic.topicId}>
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${topic.passRate}%`} color="success" size="small" />
                      </TableCell>
                      <TableCell align="right">{topic.avgScore}%</TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {topic.successFactors.map((factor, idx) => (
                            <Chip key={idx} label={factor} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label="Excellent" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Quick Mastery Details */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Mastery Topics
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Completion Time</TableCell>
                    <TableCell align="right">Estimated Time</TableCell>
                    <TableCell align="right">Time Saved</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Mastery Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quickMasteryTopics.map((topic) => {
                    const timeSaved = topic.estimatedTime - topic.completionTime;
                    return (
                      <TableRow key={topic.topicId}>
                        <TableCell>{topic.topicName}</TableCell>
                        <TableCell align="right">{topic.completionTime} min</TableCell>
                        <TableCell align="right">{topic.estimatedTime} min</TableCell>
                        <TableCell align="right">
                          <Chip label={`-${timeSaved} min`} color="success" size="small" />
                        </TableCell>
                        <TableCell align="right">{topic.avgScore}%</TableCell>
                        <TableCell align="right">{topic.masteryRate}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Best Practices */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Star color="primary" />
              <Typography variant="h6">
                Best Practices Extracted
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {bestPractices.map((practice, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {practice.practice}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {practice.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Applicable to: {practice.applicableTopics.join(', ')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Replication Opportunities */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <EmojiEvents color="primary" />
              <Typography variant="h6">
                Replication Opportunities
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Struggling Topic</TableCell>
                    <TableCell>Successful Practice</TableCell>
                    <TableCell>Expected Impact</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Trigonometry</TableCell>
                    <TableCell>Clear Step-by-Step Solutions</TableCell>
                    <TableCell>+15% pass rate improvement</TableCell>
                    <TableCell align="right">
                      <Chip label="Apply" color="primary" size="small" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Calculus Basics</TableCell>
                    <TableCell>Interactive Content</TableCell>
                    <TableCell>+20% engagement improvement</TableCell>
                    <TableCell align="right">
                      <Chip label="Apply" color="primary" size="small" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuccessAreas;

